import { inject } from "aurelia";
import moment from 'moment';
import { Modal } from 'flowbite'
import { IRouter } from '@aurelia/router';
import { actionsFromPublish, socialMediaData, subTypeData } from '@data/index';
import type { ModalOptions, ModalInterface } from 'flowbite'
import { IZeusClient, ZeusClient } from '@services/graphql-service/zeus-client';
import { IEventAggregator } from 'aurelia';
import { toastifyError, toastifySuccess } from '@helpers/toastify';
import { IFormInputOption } from "@components/form/interfaces/form-interface";

@inject(IRouter, IZeusClient, IEventAggregator)
export class PublishSocialMediaComponent {
  private modal: ModalInterface;

  private projectId: string;
  private socialMedia: string;
  private integrationId: string;
  private facebookPageId: string;
  private instagramBusinessId: string;

  private subType: string = 'feed';

  private scheduleDate: string;
  private selectedAction: string;

  private minDate: string;

  private subTypeOptions = subTypeData;
  private socialMediaOptions = socialMediaData;
  private actionsFromPublishOptions = actionsFromPublish;

  constructor(
    private router: IRouter,
    private zeusClient: ZeusClient,
    readonly ea: IEventAggregator
  ) { }

  attached() {
    this.minDate = moment().format('YYYY-MM-DDTHH:mm');

    const $modalElement: HTMLElement = document.querySelector('#modal-publish');

    const modalOptions: ModalOptions = {
      placement: 'center',
      backdrop: 'dynamic',
      backdropClasses: 'bg-gray-900 bg-opacity-50 dark:bg-opacity-80 fixed inset-0 z-40',
      closable: true,
      onShow: () => {
        this.socialMedia = null;
        this.subType = 'feed';
        this.integrationId = null;
        this.facebookPageId = null;
        this.instagramBusinessId = null;
        this.selectedAction = null;
        this.scheduleDate = null;
      },
      onHide: () => {
        this.socialMedia = null;
        this.subType = 'feed';
        this.integrationId = null;
        this.facebookPageId = null;
        this.instagramBusinessId = null;
        this.selectedAction = null;
        this.scheduleDate = null;
      }
    }

    this.modal = new Modal($modalElement, modalOptions);
  }

  private async getUserAppIntegrations(socialMedia: string) {
    const result = await this.zeusClient.getUserAppIntegrations();

    const socialMediaPages = result.filter((integration) => integration.integrationType === socialMedia)

    return {
      socialMediaPages: socialMediaPages, socialMediaOptions: socialMediaPages.map((s) => {
        return {
          value: s.id,
          label: s.alias
        } as IFormInputOption
      })
    };
  }

  private async getInstagramPages(integrationId: string) {
    const result = await this.zeusClient.getInstagramPages(integrationId);

    const pages = result.accounts.filter(x => x.instagramBusinessAccountId)

    return {
      pages: pages, pagesOptions: pages.map((p) => {
        return {
          value: p.instagramBusinessAccountId,
          label: p.name
        } as IFormInputOption
      })
    };
  }

  private async getFacebookPages(integrationId: string) {
    const result = await this.zeusClient.getFacebookPages(integrationId);

    return {
      pages: result.pages, pagesOptions: result.pages.map((p => {
        return {
          value: p.id,
          label: p.name
        } as IFormInputOption
      }))
    };
  }

  private validateForm() {
    if (!this.selectedAction) return false;

    if (this.selectedAction === 'schedule' && !this.scheduleDate) return false;

    if (!this.socialMedia) return false

    if (!this.subType) return false;

    if (!this.integrationId) return false;

    if (this.socialMedia === 'instagram' && !this.instagramBusinessId) return false;

    if (this.socialMedia === 'facebook' && !this.facebookPageId) return false;

    return true;
  }

  private async handlePublish() {
    if (!this.validateForm()) {
      toastifyError({ message: 'Preencha todos os campos' });
      return;
    }

    const { success, errorMessage } = await this.zeusClient.publishOrScheduleProject({
      channel: this.socialMedia,
      subtype: this.subType,
      projectId: this.projectId,
      integrationId: this.integrationId,
      facebookPageId: this.facebookPageId,
      instagramBusinessId: this.instagramBusinessId,
      ...this.selectedAction === 'schedule' && { scheduledDate: moment(this.scheduleDate).toDate() }
    });

    if (!success) {
      toastifyError({ message: errorMessage ?? 'Erro ao tentar publicar' });
      return;
    }

    toastifySuccess({ message: 'Publicação realizada com sucesso' });

    this.hideModal();

    await this.router.load('/projects');
  }
  async handleNoSocialMedia() {
    this.hideModal();
    await this.router.load('/social-media');
  }

  public showModal(projectId: string) {
    this.projectId = projectId;

    this.modal.show();
  }

  private hideModal() {
    this.modal.hide();
  }
}